/**
 * Vendor Payouts Page
 * Sprint 4G: Self-service payout history & tracking
 * 
 * Shows:
 * - Pending earnings (not yet batched)
 * - Payout history (all batches)
 * - Batch details (expand to see orders)
 * - Status tracking (pending → processing → paid)
 */

import { useState, useEffect } from 'react';
import { useVendorAuth } from '../hooks/useVendorAuth';
import { vendorApi } from '../lib/api';

interface PayoutBatch {
  id: string;
  vendor_id: string;
  period_start: string;
  period_end: string;
  total_payout_cents: number;
  total_orders: number;
  status: 'pending' | 'processing' | 'paid' | 'failed';
  method: string | null;
  reference_id: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export default function PayoutsPage() {
  useVendorAuth(); // Just verify auth, don't need vendor data here
  const [batches, setBatches] = useState<PayoutBatch[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedBatchId, setExpandedBatchId] = useState<string | null>(null);

  useEffect(() => {
    loadBatches();
  }, []);

  const loadBatches = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await vendorApi.getPayoutBatches();
      
      if (response.success && response.data) {
        setBatches(response.data.batches || []);
      } else {
        setError('Failed to load payouts');
      }
    } catch (err: any) {
      setError('Failed to load payouts');
      console.error('[PayoutsPage] Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'paid': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Calculate summary
  const totalPaid = batches
    .filter(b => b.status === 'paid')
    .reduce((sum, b) => sum + b.total_payout_cents, 0);
  
  const totalPending = batches
    .filter(b => b.status === 'pending' || b.status === 'processing')
    .reduce((sum, b) => sum + b.total_payout_cents, 0);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading payouts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">💰 Payouts & Earnings</h1>
          <p className="text-gray-600">Track your payments from Digital Palengke</p>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
            {error}
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Pending Earnings */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600">Pending Earnings</h3>
              <span className="text-2xl">⏳</span>
            </div>
            <div className="text-3xl font-bold text-yellow-600">
              ₱{(totalPending / 100).toFixed(2)}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Awaiting payout processing
            </p>
          </div>

          {/* Paid Earnings */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600">Total Paid</h3>
              <span className="text-2xl">✅</span>
            </div>
            <div className="text-3xl font-bold text-green-600">
              ₱{(totalPaid / 100).toFixed(2)}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Successfully received
            </p>
          </div>
        </div>

        {/* Payout History */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <h2 className="text-xl font-semibold text-gray-900">Payout History</h2>
          </div>

          {batches.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <div className="text-4xl mb-4">📦</div>
              <p className="text-lg font-medium mb-2">No payouts yet</p>
              <p className="text-sm">Your earnings will appear here once orders are completed</p>
            </div>
          ) : (
            <div className="divide-y">
              {batches.map((batch) => (
                <div key={batch.id} className="p-6">
                  {/* Batch Summary */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          ₱{(batch.total_payout_cents / 100).toFixed(2)}
                        </h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(batch.status)}`}>
                          {batch.status.toUpperCase()}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        {new Date(batch.period_start).toLocaleDateString()} - {new Date(batch.period_end).toLocaleDateString()}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {batch.total_orders} orders • Method: {batch.method || 'N/A'}
                      </div>
                      {batch.reference_id && (
                        <div className="text-sm text-gray-500 mt-1">
                          Reference: <span className="font-mono">{batch.reference_id}</span>
                        </div>
                      )}
                    </div>

                    {/* Expand Button */}
                    <button
                      onClick={() => setExpandedBatchId(expandedBatchId === batch.id ? null : batch.id)}
                      className="px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition"
                    >
                      {expandedBatchId === batch.id ? '▼ Hide Details' : '▶ View Details'}
                    </button>
                  </div>

                  {/* Expanded Details */}
                  {expandedBatchId === batch.id && (
                    <div className="mt-4 pt-4 border-t">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Orders in this batch:</h4>
                      <div className="text-sm text-gray-600">
                        {batch.total_orders} orders totaling ₱{(batch.total_payout_cents / 100).toFixed(2)}
                      </div>
                      {batch.notes && (
                        <div className="mt-3 p-3 bg-gray-50 rounded text-sm text-gray-700">
                          <strong>Notes:</strong> {batch.notes}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

